import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/17158806/pexels-photo-17158806.jpeg";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const HEADLINE = ["Engineering", "trust,", "delivering quality."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-[76px]" data-testid="hero">
      {/* Background image, clipped + parallax */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full md:w-[52%] z-0"
      >
        <div className="relative h-full w-full clip-hero">
          <img src={HERO_IMG} alt="Modern architectural facade" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent md:from-background/90" />
          <div className="absolute inset-0 bg-background/40" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-screen-2xl mx-auto px-6 md:px-12 min-h-[calc(100vh-76px)] flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/5 backdrop-blur px-4 py-2 text-xs tracking-wide text-muted-foreground mb-8"
          data-testid="hero-badge"
        >
          <ShieldCheck size={14} className="text-primary" />
          Construction Quality Inspection &amp; QA/QC Consultancy
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-heading font-light tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.95] text-foreground"
          data-testid="hero-headline"
        >
          {HEADLINE.map((line, i) => (
            <span className="line-mask" key={i}>
              <motion.span
                variants={lineChild}
                className={`inline-block ${i === 2 ? "text-primary font-medium" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
          data-testid="hero-sub"
        >
          We help builders and developers improve quality and cut costly rework — through disciplined
          inspection, standards guidance and independent, NABL-backed evidence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm font-semibold hover:bg-primary/90 hover:scale-[1.03] active:scale-95 transition-transform duration-200"
            data-testid="hero-cta-primary"
          >
            Start an inquiry
          </button>
          <button
            onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full border border-white/15 text-foreground px-8 py-3.5 text-sm font-semibold hover:bg-white/5 transition-colors duration-200"
            data-testid="hero-cta-secondary"
          >
            Explore services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-muted-foreground"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-primary" />
        </motion.div>
      </motion.div>
    </section>
  );
}
